import { supabase } from './supabase';

const SUMMARY_LIMIT_PER_DAY = 20;
const DEEPSEEK_API_URL = 'https://api.deepseek.com/v1/chat/completions';
const MAX_RETRIES = 2;
const INITIAL_RETRY_DELAY = 1000;

interface RetryOptions {
  maxRetries?: number;
  initialDelay?: number;
  shouldRetry?: (error: any) => boolean;
}

async function retryWithBackoff<T>(
  fn: () => Promise<T>,
  options: RetryOptions = {}
): Promise<T> {
  const {
    maxRetries = MAX_RETRIES,
    initialDelay = INITIAL_RETRY_DELAY,
    shouldRetry = () => true
  } = options;

  let lastError;
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      if (attempt === maxRetries - 1 || !shouldRetry(error)) {
        throw error;
      }

      const delay = initialDelay * Math.pow(2, attempt);
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  throw lastError;
}

export async function checkForSpam(issueData: {
  teacher_name: string;
  subject: string;
  details: string;
}): Promise<{ isSpam: boolean; reason?: string }> {
  try {
    const apiKey = import.meta.env.VITE_DEEPSEEK_API_KEY;
    if (!apiKey) {
      throw new Error('AI service is not available');
    }

    const prompt = `Please analyze this teacher absence report for potential spam or inappropriate content. 
    Consider the following aspects:
    1. Does the teacher's name look legitimate?
    2. Is the subject being taught realistic?
    3. Does the details section contain inappropriate, offensive, or nonsensical content? 
    4. Are there any signs of automated spam (e.g., repetitive text, random characters, advertisements)?
    5. Is the content relevant to teacher absence reporting?

    Report Data:
    Teacher Name: ${issueData.teacher_name}
    Subject: ${issueData.subject}
    Details: ${issueData.details}

    Respond with a detailed JSON object containing:
    {
      "isSpam": boolean,
      "reason": string (if isSpam is true, provide a clear explanation of why it was flagged),
      "details": {
        "teacherNameIssue": string | null,
        "subjectIssue": string | null,
        "contentIssue": string | null
      }
    }

    Be strict but fair in your assessment. Flag content that is clearly inappropriate or spam, but allow legitimate reports through.
    For rejected content, provide specific reasons that would be helpful for administrators to understand why it was flagged.`;

    const makeApiCall = async () => {
      const response = await fetch(DEEPSEEK_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: 'You are a content moderation AI that helps identify spam and inappropriate content in teacher absence reports.'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.3,
          max_tokens: 200
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
        (error as any).status = response.status;
        (error as any).type = errorData.error?.type;
        throw error;
      }

      const data = await response.json();
      const result = data.choices?.[0]?.message?.content;
      
      // Log the raw response for debugging
      console.debug('AI response:', result);

      try {
        if (!result) {
          throw new Error('No response from AI service');
        }

        // Try to extract JSON from the response
        const jsonMatch = result.match(/\{[\s\S]*\}/);
        if (!jsonMatch) {
          console.warn('No JSON found in response:', result);
          return {
            isSpam: false,
            reason: 'Unable to analyze content - invalid response format'
          };
        }

        const parsedResult = JSON.parse(jsonMatch[0]);
        
        // Validate the parsed result has the expected structure
        if (typeof parsedResult.isSpam !== 'boolean' || (parsedResult.isSpam && !parsedResult.reason)) {
          console.warn('Invalid response structure:', parsedResult);
          return {
            isSpam: false,
            reason: 'Unable to analyze content - invalid response structure'
          };
        }

        // Format a detailed rejection reason if the content is flagged
        if (parsedResult.isSpam && parsedResult.details) {
          const details = parsedResult.details;
          const reasons = [];
          
          if (details.teacherNameIssue) reasons.push(`Teacher name issue: ${details.teacherNameIssue}`);
          if (details.subjectIssue) reasons.push(`Subject issue: ${details.subjectIssue}`);
          if (details.contentIssue) reasons.push(`Content issue: ${details.contentIssue}`);
          
          const formattedReason = `AI Moderation: ${parsedResult.reason}\n\nDetailed Analysis:\n${reasons.join('\n')}`;
          return {
            isSpam: true,
            reason: formattedReason
          };
        }

        return parsedResult;
      } catch (e) {
        console.error('Error parsing AI response:', {
          error: e,
          rawResponse: result
        });

        // If parsing fails, return a safe default
        return {
          isSpam: false,
          reason: 'Unable to analyze content due to technical error'
        };
      }
    };

    const shouldRetry = (error: any) => {
      const quotaError = error.type === 'insufficient_quota' || 
                        error.code === 'insufficient_quota' ||
                        error.message?.toLowerCase().includes('quota');
      const authError = error.status === 401 || error.status === 403;
      
      if (quotaError || authError) return false;
      return error.status === 429 || (error.status >= 500 && error.status < 600);
    };

    return await retryWithBackoff(makeApiCall, { shouldRetry });
  } catch (error) {
    console.error('Error checking for spam:', {
      error: error instanceof Error ? error.message : 'Unknown error',
      stack: error instanceof Error ? error.stack : undefined
    });

    // If AI service fails, let the content through but log the error
    return {
      isSpam: false,
      reason: 'Content analysis unavailable'
    };
  }
}

export async function generateIssueSummary(issues: any[]) {
  try {
    const apiKey = import.meta.env.VITE_DEEPSEEK_API_KEY;
    if (!apiKey) {
      throw new Error('AI analysis is currently unavailable');
    }

    // Get current user
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.user?.id) {
      throw new Error('Please sign in to use AI analysis');
    }

    // Check daily limit
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const { count } = await supabase
      .from('sms_ai_summaries')
      .select('id', { count: 'exact' })
      .eq('user_id', session.user.id)
      .gte('created_at', today.toISOString());

    if ((count || 0) >= SUMMARY_LIMIT_PER_DAY) {
      throw new Error(`You've reached the daily limit of ${SUMMARY_LIMIT_PER_DAY} summaries`);
    }

    // Prepare data for DeepSeek
    const issueData = issues.map(issue => ({
      school: issue.school?.name,
      region: issue.school?.region?.name,
      subject: issue.subject,
      details: issue.details,
      status: issue.status
    }));

    const prompt = `Analyze the following teacher absence reports and provide a concise summary of the key patterns and concerns. Focus on identifying legitimate issues and ignore any potential spam or irrelevant reports.

Data:
${JSON.stringify(issueData)}

Please provide a summary that includes:
1. Most affected regions and schools
2. Common subjects with teaching gaps
3. Any notable patterns or trends
4. Recommendations if applicable

Keep the summary professional and factual.`;

    // Function to make the API call with proper error handling
    const makeApiCall = async () => {
      console.log('Making API call to DeepSeek...');
      const response = await fetch(DEEPSEEK_API_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${apiKey}`
        },
        body: JSON.stringify({
          model: 'deepseek-chat',
          messages: [
            {
              role: 'system',
              content: 'You are an educational analyst AI that helps identify patterns in teacher absence reports.'
            },
            {
              role: 'user',
              content: prompt
            }
          ],
          temperature: 0.7,
          max_tokens: 500
        })
      });

      if (!response.ok) {
        console.error('DeepSeek API error:', response.status);
        const errorData = await response.json().catch(() => ({}));
        const error = new Error(errorData.error?.message || `HTTP error! status: ${response.status}`);
        
        // Add metadata to help with retry decisions
        (error as any).status = response.status;
        (error as any).type = errorData.error?.type;
        (error as any).code = errorData.error?.code;
        
        throw error;
      }

      return response.json();
    };

    // Retry the API call with backoff, but only for specific errors
    const shouldRetry = (error: any) => {
      // Handle specific error cases
      const quotaError = error.type === 'insufficient_quota' || 
                        error.code === 'insufficient_quota' ||
                        error.message?.toLowerCase().includes('quota');
      const authError = error.status === 401 || error.status === 403;
      
      // Never retry quota or auth errors
      if (quotaError || authError) {
        if (quotaError) {
          throw new Error('AI service is temporarily unavailable due to high demand. Please try again later.');
        }
        return false;
      }
      
      // Retry on rate limits and temporary server errors
      return error.status === 429 || (error.status >= 500 && error.status < 600);
    };

    const data = await retryWithBackoff(makeApiCall, { shouldRetry });
    const summary = data.choices?.[0]?.message?.content;

    if (!summary) {
      throw new Error('Failed to generate summary. Please try again.');
    }

    // Generate a name for the summary if none was provided
    const summaryName = generateSummaryName(issues);

    // Log the summary
    await supabase
      .from('sms_ai_summaries')
      .insert({
        user_id: session.user.id,
        summary,
        issue_count: issues.length,
        name: summaryName
      });

    return {
      summary,
      remainingCount: SUMMARY_LIMIT_PER_DAY - ((count || 0) + 1)
    };
  } catch (error: any) {
    console.error('Error generating summary:', error);

    // Map error codes to user-friendly messages
    // Handle rate limiting specifically
    if (error.status === 429) {
      throw new Error('AI service is busy. Please try again in a few moments.');
    }

    // If it's already a custom error message, pass it through
    if (error instanceof Error) {
      throw error;
    }
    
    // Fallback error message
    throw new Error('An unexpected error occurred. Please try again later.');
  }
}

function generateSummaryName(issues: any[]): string {
  const now = new Date();
  const timestamp = format(now, 'MMM d, HH:mm');
  
  // Get unique schools from the issues
  const schools = [...new Set(issues.map(issue => issue.school?.name))].filter(Boolean);
  
  if (schools.length === 0) {
    return `Analysis - ${timestamp}`;
  }
  
  if (schools.length === 1) {
    return `${schools[0]} Analysis - ${timestamp}`;
  }
  
  return `Multi-School Analysis (${schools.length}) - ${timestamp}`;
}